"use client";

import { useQuery } from "@tanstack/react-query";
import { useCart } from "@/hooks/useCart";
import { Locales } from "@/lib/i18n";
import { StoreCartLineItem } from "@medusajs/types";
import { getFrenchProduct, getProductsByCategories, getRelatedPrismicProducts } from "@/lib/prismic/products";
import { __, getPermalink, resolveCategories } from "@/lib/i18n/functions";
import BestSellers from "@/components/molecules/best-sellers";
import { fetchMultiplePrismicProduct } from "@/queries/product";
import CartProductRecommendationSkeleton from "@/components/blocks/products-loading-skeleton";
import { extractKeyTextValue, isEnrichedProduct } from "@/lib/utils";
import { EnrichedProduct } from "@/types";
import { CategorieDocument, ProductDocument } from "@/prismicio-types";
import { isFilled } from "@prismicio/client";

const MAX_PRODUCTS = 8;

type RecommendedProduct = ProductDocument | EnrichedProduct;

type RecommendationItem = {
  product: RecommendedProduct;
  categorySource?: ProductDocument;
  categoryId?: string;
};

const getSuggestionCategories = (categories: CategorieDocument[]) => {
  const categoriesWithParent = categories.filter((category) =>
    isFilled.contentRelationship(category.data.parentCategorie)
  );

  return categoriesWithParent.length > 0 ? categoriesWithParent : categories;
};

const getProductUid = (product: RecommendedProduct) => (isEnrichedProduct(product) ? product.prismic.uid : product.uid);

const getProductMedusaId = (product: RecommendedProduct) =>
  isEnrichedProduct(product)
    ? extractKeyTextValue(product.prismic.medusaId)
    : extractKeyTextValue(product.data.medusaId);

const getRelatedProductIds = (products: ProductDocument[]) =>
  Array.from(
    new Set(
      products
        .flatMap((product) => product.data.relatedProducts ?? [])
        .map(({ relatedProduct }) => (isFilled.contentRelationship(relatedProduct) ? relatedProduct.id : null))
        .filter((id): id is string => Boolean(id))
    )
  );

const getFrenchSourceProduct = (product: ProductDocument, frenchProducts: ProductDocument[]) => {
  const medusaId = getProductMedusaId(product);

  return (
    frenchProducts.find((frenchProduct) => medusaId && getProductMedusaId(frenchProduct) === medusaId) ||
    frenchProducts.find((frenchProduct) =>
      frenchProduct.alternate_languages.some((alternate) => alternate.uid === product.uid)
    ) ||
    frenchProducts.find((frenchProduct) => frenchProduct.uid === product.uid)
  );
};

const isCartProduct = (product: RecommendedProduct, excludeUids: Set<string>, excludeMedusaIds: Set<string>) => {
  const productUid = getProductUid(product);
  const medusaId = getProductMedusaId(product);

  return excludeUids.has(productUid) || (medusaId ? excludeMedusaIds.has(medusaId) : false);
};

const dedupeRecommendationItems = (items: RecommendationItem[]) => {
  const deduped = new Map<string, RecommendationItem>();

  items.forEach((item) => {
    const medusaId = getProductMedusaId(item.product);
    const uid = getProductUid(item.product);
    const key = medusaId || uid;

    if (key && !deduped.has(key)) {
      deduped.set(key, item);
    }
  });

  return Array.from(deduped.values());
};

const resolveRecommendationCategories = (item: RecommendationItem, locale: Locales) => {
  if (item.categorySource) {
    return resolveCategories(item.categorySource, locale);
  }

  if (item.categoryId) {
    return resolveCategories(
      {
        data: {
          categories: [{ category: { id: item.categoryId } }],
        },
      },
      locale
    );
  }

  return isEnrichedProduct(item.product) ? [] : resolveCategories(item.product, locale);
};

const fetchRecommendations = async (cartItems: StoreCartLineItem[] | undefined, locale: Locales) => {
  if (!cartItems?.length) return null;

  const productIds = cartItems.map((item) => item.product_id).filter((id): id is string => typeof id === "string");

  if (!productIds.length) return null;

  let frenchProducts;

  // Fetch French products based on locale
  if (locale === "fr") {
    frenchProducts = await fetchMultiplePrismicProduct(locale, productIds);
  } else {
    const productPromises = productIds.map((slug) => getFrenchProduct(slug, locale));
    frenchProducts = (await Promise.all(productPromises)).filter(Boolean);
  }

  const resolvedFrenchProducts = frenchProducts?.filter((product): product is ProductDocument => Boolean(product));

  if (!resolvedFrenchProducts?.length) return null;

  // Create a set of all cart product UIDs to exclude
  const excludeUids = new Set(
    resolvedFrenchProducts.map((product) => product.uid).filter((uid): uid is string => Boolean(uid))
  );
  const excludeMedusaIds = new Set(
    resolvedFrenchProducts
      .map((product) => extractKeyTextValue(product.data.medusaId))
      .filter((medusaId): medusaId is string => Boolean(medusaId))
  );

  const relatedProductIds = getRelatedProductIds(resolvedFrenchProducts);
  const relatedProductsResult = relatedProductIds.length
    ? await getRelatedPrismicProducts(relatedProductIds, locale)
    : null;
  const relatedProducts = relatedProductsResult?.relatedProducts ?? [];
  const relatedFrenchProducts = relatedProductsResult?.frenchProducts ?? [];

  const manualRecommendationItems: RecommendationItem[] = relatedProducts.map((product) => ({
    product,
    categorySource: getFrenchSourceProduct(product, relatedFrenchProducts) || product,
  }));

  // Get specific categories from cart products. Broad root categories are too noisy for cart recommendations.
  const allCategoriesArrays = await Promise.all(
    resolvedFrenchProducts.map((product) => resolveCategories(product, "fr"))
  );

  // Flatten and deduplicate categories by ID
  const allCategories = getSuggestionCategories(allCategoriesArrays.flat());
  const uniqueCategories = Array.from(new Map(allCategories.map((cat) => [cat.id, cat])).values());

  const categoryRecommendationItems: RecommendationItem[] = uniqueCategories.length
    ? (await getProductsByCategories(uniqueCategories, "", locale)).map((product) => ({
        product,
        categoryId: product.category.id,
      }))
    : [];

  const finalItems = dedupeRecommendationItems([...manualRecommendationItems, ...categoryRecommendationItems])
    .filter((item) => !isCartProduct(item.product, excludeUids, excludeMedusaIds))
    .slice(0, MAX_PRODUCTS);

  // Get categories for the final products
  const relatedCategories = await Promise.all(finalItems.map((item) => resolveRecommendationCategories(item, locale)));

  return {
    recommendedProducts: finalItems.map((item) => item.product),
    collectionCategories: relatedCategories,
  };
};

const useCartRecommendations = (locale: Locales) => {
  const { cart } = useCart();

  return useQuery({
    queryKey: ["cart-recommendations", cart?.id, locale],
    queryFn: async () => {
      if (!cart?.items?.length) return null;
      return fetchRecommendations(cart.items, locale);
    },
    enabled: !!cart?.items?.length,
  });
};

const CartProductRecommendation = ({ locale }: { locale: Locales }) => {
  const { cart } = useCart();
  const { data, error, isLoading } = useCartRecommendations(locale);

  if (!cart?.items?.length) {
    return null;
  }

  if (isLoading) {
    return <CartProductRecommendationSkeleton itemCount={4} />;
  }

  if (error) {
    console.error("Error loading cart recommendations:", error);
    return null;
  }

  if (!data?.recommendedProducts?.length) {
    return null;
  }

  return (
    <BestSellers
      headline={__("These products may interest you", locale)}
      description=""
      linkText={__("See the entire catalogue", locale)}
      link={getPermalink(locale, "catalogue")}
      products={data.recommendedProducts}
      productCategories={data.collectionCategories}
      locale={locale}
      variant="payment"
    />
  );
};

export default CartProductRecommendation;
